import { cn, focusRingClassName, tv } from '@joyful/utils'
import { NavigationMenu as NavigationMenuPrimitive } from 'radix-ui'
import type { ComponentProps } from 'react'
import { RotatingChevron } from './Button'

export const navigationMenuTriggerVariants = tv({
  base: [
    'px-3 group inline-flex items-center justify-center gap-x-1 border border-transparent bg-transparent text-sm font-medium text-tremor-content transition-colors',
    'hover:text-tremor-content-emphasis data-[state=open]:text-tremor-content-emphasis',
    focusRingClassName,
  ],
})

export function NavigationMenu({
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Root>) {
  return (
    <NavigationMenuPrimitive.Root
      data-slot="navigation-menu"
      delayDuration={50}
      className={cn('relative z-40 flex max-w-max items-center', className)}
      {...props}
    />
  )
}

export function NavigationMenuList({
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return (
    <NavigationMenuPrimitive.List
      data-slot="navigation-menu-list"
      className={cn('group flex list-none items-center gap-x-1', className)}
      {...props}
    />
  )
}

export function NavigationMenuItem(
  props: ComponentProps<typeof NavigationMenuPrimitive.Item>,
) {
  return (
    <NavigationMenuPrimitive.Item data-slot="navigation-menu-item" {...props} />
  )
}

export function NavigationMenuTrigger({
  children,
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
  return (
    <NavigationMenuPrimitive.Trigger
      data-slot="navigation-menu-trigger"
      className={cn(navigationMenuTriggerVariants(), className)}
      {...props}
    >
      {children}
      <RotatingChevron className="relative top-px size-3" aria-hidden />
    </NavigationMenuPrimitive.Trigger>
  )
}

export function NavigationMenuContent({
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      data-slot="navigation-menu-content"
      className={cn(
        'bg-tremor-background text-tremor-content-strong data-[motion=from-end]:animate-in data-[motion=from-start]:animate-in data-[motion=to-end]:animate-out data-[motion=to-start]:animate-out absolute top-full left-0 mt-0 min-w-48 rounded-md border p-1 shadow-xs',
        'data-[motion=from-end]:slide-in-from-right-2 data-[motion=from-start]:slide-in-from-left-2 data-[motion=to-end]:slide-out-to-right-2 data-[motion=to-start]:slide-out-to-left-2',
        className,
      )}
      {...props}
    />
  )
}

export function NavigationMenuLink({
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <NavigationMenuPrimitive.Link
      data-slot="navigation-menu-link"
      className={cn(focusRingClassName, className)}
      {...props}
    />
  )
}
